<template>
  <form class="space-y-4" @submit.prevent="onSubmit">
    <div class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Document type</label>
      <select
        v-model="documentType"
        required
        class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
        :disabled="loading"
        @change="onDocumentTypeChange"
      >
        <option value="">-- Select document type --</option>
        <option v-for="option in documentTypeOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <div v-if="documentType === 'AGREEMENT'" class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Agreement sub type</label>
      <select
        v-model="agreementSubType"
        required
        class="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary"
        :disabled="loading"
      >
        <option value="">-- Select agreement sub type --</option>
        <option v-for="option in agreementSubTypeOptions" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="flex flex-col gap-1">
      <label class="text-xs font-semibold text-gray-600 uppercase tracking-wide">Import file</label>
      <input
        ref="fileInput"
        type="file"
        required
        accept=".csv,text/csv"
        class="block w-full text-sm text-gray-600 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-medium file:bg-primary file:text-white hover:file:bg-primary-600 file:cursor-pointer border border-gray-300 rounded-md bg-gray-50"
        :disabled="loading"
        @change="onFileChange"
      />
    </div>

    <div class="border-t border-gray-100 pt-2 flex justify-end">
      <button type="submit" class="btn-primary" :disabled="loading || !canSubmit">
        <i class="fa mr-1.5" :class="loading ? 'fa-spinner fa-spin' : 'fa-upload'"></i>
        {{ loading ? "Importing..." : "Import documents" }}
      </button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { api } from "@/app/services/api.service";
import { useToast } from "@/composables";

type SelectOption = { value: string; label: string };

const emit = defineEmits<{ success: [] }>();

const { successToast, errorToast, warningToast } = useToast();

const DOCUMENT_TYPES = [
  "AGREEMENT",
  "SPOUSAL_CONSENT",
  "OTHERS",
  "RESOLUTION_AS_A_DOCUMENT",
  "GUARANTEE",
  "POWER_OF_ATTORNEY",
  "CONSTITUTION",
  "DEED_POLL",
  "AFFIDAVIT",
  "STATUTORY_DECLARATION",
  "DEED_OF_ASSIGNMENT",
  "FACILITY_LETTER",
  "MORTGAGE_DEED",
  "REPORT_OF_DIRECTORS",
  "DEBENTURE",
  "CHATTEL",
  "DEMAND_PROMISSORY_NOTE",
  "INDEPENDENT_ADVISE_FORM",
  "WILL",
  "MINUTES",
  "DEED_OF_INTER_VIVOS",
  "DEED_OF_VARIATION",
  "DEED_OF_GIFT",
  "DEED_OF_INDEMNITY",
  "DEED_OF_TRUST",
  "REVOCATION_POWER_OF_ATTORNEY",
] as const;

const AGREEMENT_SUB_TYPES = [
  "CONTRACTS",
  "JOINT_VENTURE",
  "MOUs",
  "SALES_AGREEMENT",
  "TENANCY_AGREEMENT",
  "COLLATERAL_MANAGEMENT_AGREEMENT",
  "LOAN_AGREEMENT",
  "MONETARY_AND_NONE_MONETARY_AGREEMENT",
  "PARTNERSHIP_DEED",
  "OTHERS",
] as const;

const formatLabel = (value: string) =>
  value
    .split("_")
    .map((part) => {
      if (part === "MOUs") return "MOUs";
      return part.charAt(0) + part.slice(1).toLowerCase();
    })
    .join(" ");

const documentTypeOptions: SelectOption[] = DOCUMENT_TYPES.map((value) => ({
  value,
  label: formatLabel(value),
}));

const agreementSubTypeOptions: SelectOption[] = AGREEMENT_SUB_TYPES.map((value) => ({
  value,
  label: formatLabel(value),
}));

const loading = ref(false);
const documentType = ref("");
const agreementSubType = ref("");
const file = ref<File | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);

const canSubmit = computed(() => {
  if (!documentType.value || !file.value) return false;
  if (documentType.value === "AGREEMENT" && !agreementSubType.value) return false;
  return true;
});

const extractErrorMessage = (error: unknown, fallback: string) => {
  const err = error as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || err.message || fallback;
};

const onDocumentTypeChange = () => {
  if (documentType.value !== "AGREEMENT") {
    agreementSubType.value = "";
  }
};

const onFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const selected = target.files?.[0] ?? null;
  if (selected && !selected.name.toLowerCase().endsWith(".csv")) {
    warningToast("Validation", "Only CSV files are allowed");
    file.value = null;
    if (fileInput.value) fileInput.value.value = "";
    return;
  }
  file.value = selected;
};

const resetForm = () => {
  documentType.value = "";
  agreementSubType.value = "";
  file.value = null;
  if (fileInput.value) fileInput.value.value = "";
};

const onSubmit = async () => {
  if (!documentType.value) {
    warningToast("Validation", "Select a document type");
    return;
  }
  if (documentType.value === "AGREEMENT" && !agreementSubType.value) {
    warningToast("Validation", "Select an agreement sub type");
    return;
  }
  if (!file.value) {
    warningToast("Validation", "Select a CSV file to import");
    return;
  }
  if (!file.value.name.toLowerCase().endsWith(".csv")) {
    warningToast("Validation", "Only CSV files are allowed");
    return;
  }

  const params: Record<string, string> = {
    documentType: documentType.value,
  };
  if (documentType.value === "AGREEMENT") {
    params.agreementSubType = agreementSubType.value;
  }

  const formData = new FormData();
  formData.append("file", file.value);

  loading.value = true;
  try {
    const response = await api().post("/legal-documents/doc-import", formData, {
      params,
      headers: { "Content-Type": "multipart/form-data" },
    });
    const message =
      (response?.data as { message?: string } | undefined)?.message ||
      "Legal documents imported successfully";
    successToast("Success", message);
    resetForm();
    emit("success");
  } catch (error: unknown) {
    errorToast("Error", extractErrorMessage(error, "Could not import legal documents"));
  } finally {
    loading.value = false;
  }
};
</script>
